import { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { Box, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import moment from 'moment';
import { getNextPublishingDate } from '../../../../../../utils/automationSchedule';
import { AppContext } from '../../../../../../context';
import { scrollElementIntoScrollableColumn } from '../../../../debugMonthlyCommentary';

interface Props {
  data: any;
  onChange: (data: any) => void;
  onRegisterFlush?: (flush: () => void) => void;
  isAutomated?: boolean;
  lastPublishedAt?: string | null;
  automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
}

const iconSx = {
  width: 20,
  height: 20,
  cursor: 'pointer',
  transition: 'color 120ms ease',
  '&:hover': { color: 'secondary.dark' },
} as const;

/** Stable card wrapper — must live outside PdfConfig or every keystroke remounts the form. */
const ConfigCard: FC<{ children: ReactNode }> = ({ children }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Stack spacing={1.5}>{children}</Stack>
    </CardContent>
  </Card>
);

export const PdfConfig: FC<Props> = ({
  data,
  onChange,
  onRegisterFlush,
  isAutomated = false,
  lastPublishedAt = null,
  automationFrequency = 'monthly',
}) => {
  const { document } = useContext(AppContext);
  const [formData, setFormData] = useState(data || {});

  useEffect(() => {
    setFormData(data || {});
  }, [data]);

  useEffect(() => {
    onRegisterFlush?.(() => {
      setFormData((current) => {
        onChange(current);
        return current;
      });
    });
  }, [onChange, onRegisterFlush]);

  const update = (patch: Record<string, unknown>) => {
    const next = { ...formData, ...patch };
    setFormData(next);
    onChange(next);
  };

  const nextPublish = (() => {
    if (!isAutomated || !lastPublishedAt) return '';
    const base = moment(lastPublishedAt);
    if (!base.isValid()) return '';
    return getNextPublishingDate(base, automationFrequency).format('DD.MM.YYYY');
  })();

  const isin = document?.template?.isins?.[0] || formData?.stammdaten?.isin || '';

  const handleScrollTo = (sectionId: string) => {
    const element = window.document.getElementById(sectionId);
    if (!element) return;
    scrollElementIntoScrollableColumn(element);
  };

  const sectionTitle = (label: string, sectionId: string) => (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <OpenInNew onClick={() => handleScrollTo(sectionId)} color="secondary" sx={iconSx} />
    </Box>
  );

  return (
    <Box sx={{ p: 2, my: 64, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography variant="h6" gutterBottom>
        PDF Configuration
      </Typography>

      <ConfigCard>
        {sectionTitle('Document', 'pr-header-section')}
        <TextField
          fullWidth
          size="small"
          label="Title"
          value={formData.title || ''}
          onChange={(e) => update({ title: e.target.value })}
        />
        <Typography variant="caption" color="text.secondary" display="block">
          ISIN: {isin || '—'} · Share class: {formData.prShareClass || '—'} · Language:{' '}
          {formData.prLocale === 'en' ? 'EN' : 'DE'}
        </Typography>
        {isAutomated && (
          <Typography variant="caption" color="text.secondary" display="block">
            Last published: {lastPublishedAt ? moment(lastPublishedAt).format('DD.MM.YYYY HH:mm') : '—'}
            {nextPublish ? ` · Next scheduled: ${nextPublish}` : ''}
          </Typography>
        )}
      </ConfigCard>

      <ConfigCard>
        {sectionTitle('Investment style', 'pr-investment-style-section')}
        <TextField
          fullWidth
          multiline
          minRows={6}
          value={formData.investmentStyle || ''}
          onChange={(e) => update({ investmentStyle: e.target.value })}
        />
      </ConfigCard>

      {formData.prShareClass === 'R' && (
        <ConfigCard>
          {sectionTitle('Share-class history note (R)', 'pr-performance-section')}
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={formData.shareClassHistoryNote || ''}
            onChange={(e) => update({ shareClassHistoryNote: e.target.value })}
          />
        </ConfigCard>
      )}

      <ConfigCard>
        {sectionTitle('Disclaimer', 'pr-disclaimer-section')}
        <TextField
          fullWidth
          multiline
          minRows={10}
          value={formData.disclaimer || ''}
          onChange={(e) => update({ disclaimer: e.target.value })}
        />
      </ConfigCard>
    </Box>
  );
};
